'use client';

import { useRouter } from 'next/navigation';

import type { CreatorInfo } from '@ps/types/creator';
import {
  useAuthContext,
  useIsSignedInNonGuest,
  useLogin,
} from '@ps/ui/contexts/auth';
import { cn } from '@ps/ui/utils';
import { UserRound } from 'lucide-react';

import { kIframePreviewHiddenProps } from '../../iframe-preview/BioIframePreviewMode';

interface Props {
  /** Creator identity, handed to the intercepted login modal as brand chrome. */
  creatorInfo: CreatorInfo;
  className?: string;
}

/**
 * Visitor login / account pill for the top-right of the readonly bio header.
 *
 * A client island on an otherwise server-rendered page: it reads the signed-in
 * state from `useIsSignedInNonGuest` (supplied by the bio's
 * `AuthProvider`) — a throwaway guest account from guest checkout has a
 * uid but is NOT treated as logged in — and routes through app-level entry
 * points, never a per-bio route.
 *
 *   - Logged out → "Login" via `useLogin`, handing the creator's `CreatorInfo`
 *     to the intercepted `@login/(.)login` as brand chrome. On success, a creator
 *     who just logged in on their OWN bio is sent straight into edit mode
 *     (`/my-popstore`); any other viewer returns to the bio.
 *   - Signed-in creator → `/dashboard` (hard-nav, leaves the bio).
 *   - Signed-in fan → `/my-account` (`router.push` so the `@modal/(.)my-account`
 *     intercept overlays the dialog on the still-mounted bio).
 *
 * Creator-vs-fan needs the viewer's `userType`, but the bio keeps its profile /
 * `firebase/firestore` OFF the visitor bundle (auth-only). So the decision is
 * resolved on click by the `/api/account-destination` JSON endpoint (server
 * reads the session + profile, applies RN's `isCreatorProfile`). Mirrors
 * PopshopRN's `MyAccountButton` (`isCreatorProfile ? creatorStudio : myAccount`).
 *
 * While auth is still resolving the pill reads as logged-out ("Login") — the
 * correct default for the overwhelmingly-anonymous audience.
 */
export function AccountPillIsland({ creatorInfo, className }: Props) {
  const router = useRouter();
  const login = useLogin();
  // A guest account (guest checkout) has a uid but must NOT read as logged in —
  // exclude it so the pill shows "Login", not "Account".
  const loggedIn = useIsSignedInNonGuest();
  // Read the uid at login-success time (not at click time): the callback fires
  // after `onAuthenticated`, so auth has resolved and the subject holds the
  // freshly signed-in uid.
  const { currentUserId$ } = useAuthContext();

  const onClick = async () => {
    const here = window.location.pathname + window.location.search;

    if (!loggedIn) {
      login({
        creatorInfo,
        next: here,
        // A creator logging in on their OWN bio lands straight in edit mode
        // (`/my-popstore`, a hard-nav out of the visitor tree). Any other viewer
        // just returns to the bio — the default `land()` `router.back()`.
        onSuccess: () => {
          if (currentUserId$.getValue() === creatorInfo.id) {
            window.location.href = '/my-popstore';
          } else {
            router.back();
          }
        },
      });
      return;
    }

    const res = await fetch('/api/account-destination', { cache: 'no-store' });
    const { path } = (await res.json()) as { path?: string };
    if (path === '/dashboard') {
      window.location.href = '/dashboard';
    } else {
      router.push(`/my-account?from=${encodeURIComponent(here)}`);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-[12px] font-semibold text-white shadow-md backdrop-blur transition hover:bg-black/70',
        className,
      )}
      aria-label={loggedIn ? 'Open my account' : 'Log in'}
      // Embedded preview (`?source=iframe`): logging in / opening the account
      // modal inside the host's phone mockup is never what the viewer wants.
      {...kIframePreviewHiddenProps}
    >
      <UserRound className="h-4 w-4" aria-hidden />
      <span>{loggedIn ? 'Account' : 'Login'}</span>
    </button>
  );
}
